"use client";

import Image from "next/image";
import { useState } from "react";

import { cn } from "~/lib/utils";
import PlusThin from "@/icons/actions/plus-thin.svg";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";

type ToggleIconProps = {
    opened: boolean;
};

type ImageBlockProps = {
    image: string;
    imageAlt: string;
    imageClassName: string;
};

type FaceLayout = {
    outerClassName: string;
    innerClassName: string;
};

type ClosedFaceProps = ImageBlockProps &
    FaceLayout & {
        title: string;
        mobileTitle?: string;
    };

type OpenedFaceProps = ImageBlockProps &
    FaceLayout & {
        title: string;
        description: string;
        descriptionMobile?: string;
        descriptionClassName: string;
        mobileSpacerClassName: string;
    };

export type TSourceCard = {
    id?: string;
    title: string;
    mobileTitle?: string;
    image: string;
    imageAlt: string;
    description: string;
    descriptionMobile?: string;
};

type SourceCardProps = TSourceCard & {
    className?: string;
    variant?: "default" | "compact";
    gtm?: {
        eventName?: string;
        widget?: string;
    };
};

const ToggleIcon = ({ opened }: ToggleIconProps) => (
    <div
        className={cn(
            "absolute right-0 top-0 z-20 flex h-[35px] w-[35px] items-center justify-center rounded-full border-[1.5px] backdrop-blur-[7.5px] transition-transform duration-300",
            opened ? "rotate-45 border-pureWhite/25 bg-pureWhite/5" : "border-transparent bg-[#2446AA]"
        )}
    >
        <PlusThin width={22} height={22} className="text-pureWhite" />
    </div>
);

const ImageBlock = ({ image, imageAlt, imageClassName }: ImageBlockProps) => (
    <div className={cn("relative shrink-0 overflow-hidden rounded-[12px] transition-all duration-300 ease-in-out group-hover:scale-105", imageClassName)}>
        <Image
            src={image}
            alt={imageAlt}
            fill
            className="object-cover"
            sizes="132px"
        />
    </div>
);

const ClosedFace = ({ title, mobileTitle, image, imageAlt, imageClassName, outerClassName, innerClassName }: ClosedFaceProps) => (
    <div className={cn("relative flex gap-4", outerClassName)}>
        <ImageBlock image={image} imageAlt={imageAlt} imageClassName={imageClassName} />

        <div className={cn("relative flex min-w-0 flex-1 flex-col", innerClassName)}>
            <div className="flex min-h-[140px] min-w-0 flex-1">
                <div className="flex min-w-0 flex-1 flex-col justify-start">
                    <h3 className="hidden lg:block text-[16px] uppercase leading-[1.25] tracking-[1.28px] lg:w-[362px] lg:whitespace-pre-line">
                        {title}
                    </h3>
                    <p className="lg:hidden text-[16px] font-normal uppercase leading-[1.25] tracking-[1.28px] whitespace-pre-line">
                        {mobileTitle ?? title}
                    </p>
                </div>
            </div>
        </div>

        <ToggleIcon opened={false} />
    </div>
);

const OpenedFace = ({ title, description, descriptionMobile, image, imageAlt, imageClassName, outerClassName, innerClassName, descriptionClassName, mobileSpacerClassName }: OpenedFaceProps) => (
    <div className={cn("relative flex gap-3", outerClassName)}>
        <ImageBlock image={image} imageAlt={imageAlt} imageClassName={imageClassName} />

        <div className={cn("relative flex min-w-0 flex-1 flex-col lg:pr-14", innerClassName)}>
            <div className="min-w-0 flex flex-col lg:h-full lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1 lg:hidden">
                    <p className="whitespace-pre-line text-[18px] leading-[1.2]">
                        <span aria-hidden className={cn("float-right block", mobileSpacerClassName)} />
                        {descriptionMobile ?? description}
                    </p>
                    <div className="clear-both" />
                </div>

                <p className="hidden text-[16px] uppercase leading-[1.25] tracking-[1.28px] lg:block lg:w-[362px] lg:whitespace-pre-line">
                    {title}
                </p>

                <p className={cn("mt-3 hidden w-full min-w-0 leading-[1.2] lg:block", descriptionClassName)}>
                    {description}
                </p>
            </div>
        </div>

        <ToggleIcon opened />
    </div>
);

const SourceCard = ({ title, mobileTitle, description, descriptionMobile, image, imageAlt, className, variant = "default", gtm }: SourceCardProps) => {
    const sendEvent = useTrackEvent();
    const [isOpened, setIsOpened] = useState(false);

    const isCompact = variant === "compact";
    const imageClassName = isCompact ? "h-[132px] w-[132px]" : "h-[140px] w-[132px]";
    const closedOuterClassName = isCompact ? "h-full items-end lg:items-start" : "h-full items-start";
    const closedInnerClassName = isCompact ? "lg:h-full" : "h-full pr-10";
    const openedOuterClassName = isCompact ? "h-full items-end lg:items-start" : "h-full items-start";
    const openedInnerClassName = isCompact ? "lg:h-full" : "h-full";
    const descriptionClassName = isCompact ? "text-[18px]" : "text-[22px]";
    const mobileSpacerClassName = isCompact ? "w-[42px] h-8" : "ml-2 h-[43px] w-[43px]";
    const darkFaceClassName = isCompact ? "rounded-[12px] bg-[#252355]" : "rounded-[18px] bg-[#14244F]";
    const cardHeightClassName = isCompact ? "h-[164px]" : "h-[172px]";

    const handleToggle = () => {
        if (!isOpened) {
            sendEvent({
                event: "user_interaction",
                value: {
                    event_name: gtm?.eventName ?? GTM_EVENT_NAMES.LP_INTERACTION,
                    widget: gtm?.widget ?? "our_sources",
                    meta_data: {
                        click_text: title,
                    },
                },
            });
        }

        setIsOpened(prev => !prev);
    };

    return (
        <button
            type="button"
            aria-expanded={isOpened}
            onClick={handleToggle}
            className={cn(
                "group relative w-full shrink-0 rounded-[12px] border-0 bg-transparent p-0 text-left font-suisseBP appearance-none",
                cardHeightClassName,
                className
            )}
        >
            <div className="grid [perspective:1000px] relative h-full w-full rounded-[12px]">
                <div
                    aria-hidden={isOpened}
                    className={cn(
                        "col-start-1 row-start-1 rounded-[12px] bg-primary100 p-4 text-primary500 duration-300",
                        isOpened ? "opacity-0" : "opacity-100",
                    )}
                    style={{
                        transform: isOpened ? "translateY(50%) rotateX(-90deg)" : "translateY(0) rotateX(0deg)",
                    }}
                >
                    <ClosedFace
                        title={title}
                        mobileTitle={mobileTitle}
                        image={image}
                        imageAlt={imageAlt}
                        imageClassName={imageClassName}
                        outerClassName={closedOuterClassName}
                        innerClassName={closedInnerClassName}
                    />
                </div>

                <div
                    aria-hidden={!isOpened}
                    className={cn(
                        "col-start-1 row-start-1 p-4 text-white duration-300",
                        darkFaceClassName,
                        isOpened ? "opacity-100" : "opacity-0",
                    )}
                    style={{
                        transform: isOpened ? "translateY(0) rotateX(0deg)" : "translateY(-50%) rotateX(90deg)",
                    }}
                >
                    <OpenedFace
                        title={title}
                        description={description}
                        descriptionMobile={descriptionMobile}
                        image={image}
                        imageAlt={imageAlt}
                        imageClassName={imageClassName}
                        outerClassName={openedOuterClassName}
                        innerClassName={openedInnerClassName}
                        descriptionClassName={descriptionClassName}
                        mobileSpacerClassName={mobileSpacerClassName}
                    />
                </div>
            </div>
        </button>
    );
}

export default SourceCard;
